import { useEffect, useState } from 'react';
import type { AssetCategory, AssetStatus } from '../lib/api/assets';

export interface AssetFilters {
  statuses: AssetStatus[];
  categoryCodes: string[];
  departments: string[];
}

export const EMPTY_FILTERS: AssetFilters = { statuses: [], categoryCodes: [], departments: [] };

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (filters: AssetFilters) => void;
  initial: AssetFilters;
  categories: AssetCategory[];
  /** List distinct departments đọc từ dataset TS (compute ở parent). */
  departmentOptions: string[];
}

const STATUS_OPTIONS: Array<{ value: AssetStatus; label: string; dot: string }> = [
  { value: 'in_use', label: 'Đang dùng', dot: '#22c55e' },
  { value: 'maintenance', label: 'Bảo trì', dot: '#f59e0b' },
  { value: 'depleted', label: 'Hết KH', dot: '#94a3b8' },
  { value: 'disposed', label: 'Đã thanh lý', dot: '#dc2626' },
];

export function AssetFilterModal({ open, onClose, onApply, initial, categories, departmentOptions }: Props) {
  const [statuses, setStatuses] = useState<AssetStatus[]>(initial.statuses);
  const [categoryCodes, setCategoryCodes] = useState<string[]>(initial.categoryCodes);
  const [departments, setDepartments] = useState<string[]>(initial.departments);

  useEffect(() => {
    if (!open) return;
    setStatuses(initial.statuses);
    setCategoryCodes(initial.categoryCodes);
    setDepartments(initial.departments);
  }, [open, initial]);

  if (!open) return null;

  const toggle = <T extends string>(arr: T[], v: T, setter: (next: T[]) => void) => {
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  const handleApply = () => {
    onApply({ statuses, categoryCodes, departments });
    onClose();
  };
  const handleReset = () => {
    setStatuses([]); setCategoryCodes([]); setDepartments([]);
  };

  const totalPicked = statuses.length + categoryCodes.length + departments.length;

  return (
    <div onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={headerIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"><line x1="21" x2="14" y1="4" y2="4" /><line x1="10" x2="3" y1="4" y2="4" /><line x1="21" x2="12" y1="12" y2="12" /><line x1="8" x2="3" y1="12" y2="12" /><line x1="21" x2="16" y1="20" y2="20" /><line x1="12" x2="3" y1="20" y2="20" /></svg>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#172033' }}>Bộ lọc tài sản</h3>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#667085' }}>Chọn nhiều điều kiện · Kết quả là AND giữa nhóm, OR trong cùng nhóm</p>
            </div>
          </div>
          <button type="button" onClick={onClose} style={closeBtn}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div style={body}>
          <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FilterGroup title="Trạng thái">
              {STATUS_OPTIONS.map((s) => (
                <FilterChip
                  key={s.value}
                  active={statuses.includes(s.value)}
                  onClick={() => toggle(statuses, s.value, setStatuses)}
                >
                  <span style={{ width: 8, height: 8, background: s.dot, borderRadius: 9999, display: 'inline-block' }} />
                  {s.label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup title="Loại tài sản">
              {categories.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8' }}>Chưa có loại nào</span>}
              {categories.map((c) => (
                <FilterChip
                  key={c.code}
                  active={categoryCodes.includes(c.code)}
                  onClick={() => toggle(categoryCodes, c.code, setCategoryCodes)}
                >
                  <span style={{ padding: '1px 6px', background: '#eff6ff', color: '#1d4ed8', fontSize: 10, fontWeight: 700, borderRadius: 3 }}>{c.code}</span>
                  {c.name}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup title="Bộ phận">
              {departmentOptions.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8' }}>Không có dữ liệu</span>}
              {departmentOptions.map((d) => (
                <FilterChip
                  key={d}
                  active={departments.includes(d)}
                  onClick={() => toggle(departments, d, setDepartments)}
                >
                  {d}
                </FilterChip>
              ))}
            </FilterGroup>
          </div>
        </div>

        <div style={footer}>
          <button type="button" onClick={handleReset} disabled={totalPicked === 0} style={{ ...cancelBtn, opacity: totalPicked === 0 ? 0.5 : 1 }}>
            Xoá tất cả
          </button>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Huỷ</button>
            <button type="button" onClick={handleApply} style={applyBtn}>
              Áp dụng {totalPicked > 0 && <span style={{ padding: '1px 8px', background: 'rgba(255,255,255,.2)', borderRadius: 9999, fontSize: 12 }}>{totalPicked}</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#667085', fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        background: active ? '#eff6ff' : '#fff',
        color: active ? '#1d4ed8' : '#334155',
        border: `1px solid ${active ? '#2563EB' : '#e4e7ec'}`,
        borderRadius: 9999,
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
      }}
    >
      {children}
      {active && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
      )}
    </button>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
  zIndex: 65, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalBox: React.CSSProperties = {
  width: 560, maxWidth: '100%', maxHeight: 'calc(100vh - 48px)',
  display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 12,
  boxShadow: '0 25px 60px -12px rgba(15,23,42,.5)', overflow: 'hidden',
};
const header: React.CSSProperties = {
  flex: '0 0 auto', padding: '16px 22px', borderBottom: '1px solid #e4e7ec',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const headerIcon: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 8, background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const closeBtn: React.CSSProperties = {
  width: 30, height: 30, background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8,
  cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', background: '#f8fafc', minHeight: 0 };
const footer: React.CSSProperties = {
  flex: '0 0 auto', padding: '12px 22px', borderTop: '1px solid #e4e7ec', background: '#fff',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 14px', background: '#fff', border: '1px solid #e4e7ec', color: '#334155',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
const applyBtn: React.CSSProperties = {
  padding: '9px 18px', background: '#2563EB', color: '#fff', fontSize: 13, fontWeight: 600,
  border: 'none', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};
